import React, { useCallback, useEffect, useRef, useState } from "react";
import { GOOGLE_MAPS_API_KEY } from "../utils/constants";

interface Location {
  lat: number;
  lng: number;
}

interface LiveTrackingMapProps {
  restaurantLocation?: Location | null;
  deliveryLocation?: Location | null;
  driverLocation?: Location | null;
  restaurantLabel?: string;
  deliveryLabel?: string;
  googleMapsApiKey?: string;
}

declare global {
  interface Window {
    google: any;
    gm_authFailure?: () => void;
  }
}

const pinSvg = (fill: string, letter: string) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
      <path fill="${fill}" d="M20 0C9 0 0 9 0 20c0 15 20 32 20 32s20-17 20-32C40 9 31 0 20 0z"/>
      <circle cx="20" cy="19" r="10" fill="#ffffff"/>
      <text x="20" y="24" text-anchor="middle" font-size="13" font-weight="700" fill="${fill}" font-family="Arial,sans-serif">${letter}</text>
    </svg>
  `.trim();
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const RESTAURANT_PIN = pinSvg("#7402A7", "R");
const DELIVERY_PIN = pinSvg("#10B981", "D");

const LiveTrackingMap: React.FC<LiveTrackingMapProps> = ({
  restaurantLocation = null,
  deliveryLocation = null,
  driverLocation = null,
  restaurantLabel = "Restaurant",
  deliveryLabel = "Delivery location",
  googleMapsApiKey = GOOGLE_MAPS_API_KEY,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const driverMarkerRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const generateCurvedPath = useCallback(
    (start: Location, end: Location, curvature: number): Location[] => {
      const points: Location[] = [];
      const numPoints = 50;
      const midLat = (start.lat + end.lat) / 2;
      const midLng = (start.lng + end.lng) / 2;
      const dLat = end.lat - start.lat;
      const dLng = end.lng - start.lng;
      const distance = Math.sqrt(dLat * dLat + dLng * dLng) || 0.0001;
      const perpLat = -dLng / distance;
      const perpLng = dLat / distance;
      const curveOffset = distance * curvature;
      const controlLat = midLat + perpLat * curveOffset;
      const controlLng = midLng + perpLng * curveOffset;

      for (let i = 0; i <= numPoints; i++) {
        const t = i / numPoints;
        const invT = 1 - t;
        points.push({
          lat:
            invT * invT * start.lat +
            2 * invT * t * controlLat +
            t * t * end.lat,
          lng:
            invT * invT * start.lng +
            2 * invT * t * controlLng +
            t * t * end.lng,
        });
      }
      return points;
    },
    []
  );

  const restaurantLat = restaurantLocation?.lat ?? null;
  const restaurantLng = restaurantLocation?.lng ?? null;
  const deliveryLat = deliveryLocation?.lat ?? null;
  const deliveryLng = deliveryLocation?.lng ?? null;

  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google?.maps) return;

    const restaurant =
      restaurantLat != null && restaurantLng != null
        ? { lat: restaurantLat, lng: restaurantLng }
        : null;
    const delivery =
      deliveryLat != null && deliveryLng != null
        ? { lat: deliveryLat, lng: deliveryLng }
        : null;
    if (!restaurant && !delivery) {
      setHasError(true);
      setIsLoading(false);
      return;
    }

    const isDarkMode = document.documentElement.classList.contains("dark");
    const lineColor = isDarkMode ? "#7402A7" : "#000000";
    const center =
      restaurant && delivery
        ? {
            lat: (restaurant.lat + delivery.lat) / 2,
            lng: (restaurant.lng + delivery.lng) / 2,
          }
        : restaurant || delivery!;

    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 13,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: true,
      gestureHandling: "greedy",
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }],
        },
      ],
    });

    const pinIcon = (url: string) => ({
      url,
      scaledSize: new window.google.maps.Size(40, 52),
      anchor: new window.google.maps.Point(20, 52),
    });

    if (restaurant) {
      new window.google.maps.Marker({
        position: restaurant,
        map: mapInstanceRef.current,
        icon: pinIcon(RESTAURANT_PIN),
        title: restaurantLabel,
        zIndex: 2,
      });
    }

    if (delivery) {
      new window.google.maps.Marker({
        position: delivery,
        map: mapInstanceRef.current,
        icon: pinIcon(DELIVERY_PIN),
        title: deliveryLabel,
        zIndex: 2,
      });
    }

    if (restaurant && delivery) {
      const sameSpot =
        Math.abs(restaurant.lat - delivery.lat) < 0.00001 &&
        Math.abs(restaurant.lng - delivery.lng) < 0.00001;

      if (!sameSpot) {
        new window.google.maps.Polyline({
          path: generateCurvedPath(restaurant, delivery, 0.3),
          geodesic: false,
          strokeColor: lineColor,
          strokeOpacity: 0.8,
          strokeWeight: 3,
          map: mapInstanceRef.current,
        });
      }
    }

    const bounds = new window.google.maps.LatLngBounds();
    if (restaurant) bounds.extend(restaurant);
    if (delivery) bounds.extend(delivery);
    mapInstanceRef.current.fitBounds(bounds, 56);

    setIsLoading(false);
  }, [
    restaurantLat,
    restaurantLng,
    deliveryLat,
    deliveryLng,
    restaurantLabel,
    deliveryLabel,
    generateCurvedPath,
  ]);

  useEffect(() => {
    if (!driverLocation || !mapInstanceRef.current || !window.google) return;

    if (!driverMarkerRef.current) {
      driverMarkerRef.current = new window.google.maps.Marker({
        position: driverLocation,
        map: mapInstanceRef.current,
        icon: {
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 6,
          fillColor: "#FB8606",
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
          rotation: 0,
        },
        title: "Delivery Partner",
      });
    } else {
      driverMarkerRef.current.setPosition(driverLocation);
    }
  }, [driverLocation]);

  useEffect(() => {
    let cancelled = false;

    window.gm_authFailure = () => {
      if (!cancelled) {
        setHasError(true);
        setIsLoading(false);
      }
    };

    const loadMaps = () => {
      if (window.google?.maps) {
        if (!cancelled) initializeMap();
        return;
      }

      const existing = document.querySelector(
        'script[data-duddus-maps="true"]'
      ) as HTMLScriptElement | null;

      if (existing) {
        existing.addEventListener("load", () => {
          if (!cancelled) initializeMap();
        });
        existing.addEventListener("error", () => {
          if (!cancelled) {
            setHasError(true);
            setIsLoading(false);
          }
        });
        return;
      }

      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.dataset.duddusMaps = "true";
      script.onload = () => {
        if (!cancelled) initializeMap();
      };
      script.onerror = () => {
        if (!cancelled) {
          setHasError(true);
          setIsLoading(false);
        }
      };
      document.head.appendChild(script);
    };

    loadMaps();
    return () => {
      cancelled = true;
    };
  }, [googleMapsApiKey, initializeMap]);

  if (hasError) {
    return (
      <div className="w-full h-[280px] md:h-[360px] flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-[16px]">
        <p className="text-gray-500 dark:text-gray-400 text-center px-4">
          Unable to load map. Check that Maps JavaScript API is enabled for this key.
        </p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[280px] md:h-[360px] rounded-[16px] overflow-hidden">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
          <p className="text-gray-500 dark:text-gray-400">Loading map...</p>
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
      {!isLoading && (
        <div className="absolute left-3 bottom-3 z-10 flex flex-col gap-1.5 rounded-xl bg-white/95 dark:bg-gray-800/95 shadow-md px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-[#7402A7]" />
            <span className="text-xs font-urbanist font-semibold text-[#111] dark:text-white">
              Restaurant
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-[#10B981]" />
            <span className="text-xs font-urbanist font-semibold text-[#111] dark:text-white">
              Delivery
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveTrackingMap;
